import React, { useEffect, useState } from 'react';
import { CalendarClock, MapPin, Users, Loader2, X, Ticket } from 'lucide-react';
import AccessGate from '../components/AccessGate';
import EventPhotoStrip from '../components/EventPhotoStrip';
import { useAuth } from '../context/AuthContext';

function fmt(dt) { if (!dt) return ''; try { return new Date(dt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }); } catch { return dt; } }

function RegisterModal({ ev, onClose }) {
  const { registerEvent, user } = useAuth();
  const [form, setForm] = useState({ name: user?.name || '', email: user?.email || '', guests: 0, notes: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const submit = async () => {
    setBusy(true); setMsg('');
    try {
      const res = await registerEvent(ev.id, { ...form, guests: Number(form.guests) || 0, origin_url: window.location.origin });
      if (res.free) { setMsg('You are registered! A confirmation email is on its way.'); setTimeout(onClose, 1600); }
      else if (res.checkout_url) { window.location.href = res.checkout_url; }
    } catch (e) { setMsg((e.response && e.response.data && e.response.data.detail) || 'Could not register.'); }
    setBusy(false);
  };
  const total = ((ev.applicable_price || 0) * (1 + (Number(form.guests) || 0)));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }} data-testid="event-register-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-title text-lg" style={{ color: 'var(--maroon)' }}>Register — {ev.title}</h3>
          <button onClick={onClose} data-testid="event-register-close"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <input className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="reg-name" />
          <input className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="reg-email" />
          <label className="text-sm block" style={{ color: 'var(--ink)' }}>Additional guests
            <input type="number" min={0} max={20} className="w-full px-3 py-2 rounded border text-sm mt-1" style={{ borderColor: 'rgba(0,0,0,0.2)' }} value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} data-testid="reg-guests" />
          </label>
          <textarea className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} rows={2} placeholder="Notes (dietary requirements, etc.)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="reg-notes" />
          {ev.applicable_price > 0 && (
            <p className="text-sm" style={{ color: 'var(--navy)' }}>
              Total: <strong>${total.toFixed(2)} AUD</strong> {ev.is_member && ev.member_price < ev.price && <span style={{ color: 'var(--olive)' }}>(member price)</span>}
            </p>
          )}
          {msg && <p className="text-sm" data-testid="reg-msg" style={{ color: msg.includes('registered') ? '#1c6b2c' : '#8a1f11' }}>{msg}</p>}
          <button onClick={submit} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: busy ? 0.7 : 1 }} data-testid="reg-submit">
            {busy && <Loader2 size={15} className="animate-spin" />}
            {ev.applicable_price > 0 ? 'Register & pay' : 'Register'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Events() {
  const { listEvents } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reg, setReg] = useState(null);
  useEffect(() => { listEvents().then(setEvents).catch(() => setEvents([])).finally(() => setLoading(false)); }, [listEvents]);
  const upcoming = events.filter((e) => !e.ended);
  const past = events.filter((e) => e.ended).reverse();

  return (
    <AccessGate pageKey="events">
      <div className="max-w-4xl mx-auto px-4 py-10 fade-in">
        <h1 className="font-title text-3xl md:text-4xl" style={{ color: 'var(--maroon)' }}>Events</h1>
        <div className="h-1 w-20 mt-2 mb-6" style={{ background: 'var(--gold)' }} />
        {loading ? <p className="text-sm" style={{ color: 'var(--olive)' }}>Loading…</p>
          : events.length === 0 ? <p className="text-sm" style={{ color: 'var(--olive)' }} data-testid="events-empty">No upcoming events right now — check back soon.</p>
          : (
          <div className="space-y-4" data-testid="events-list">
            {upcoming.length === 0 && <p className="text-sm" style={{ color: 'var(--olive)' }} data-testid="events-none-upcoming">No upcoming events right now — check back soon.</p>}
            {[...upcoming, ...past].map((ev, i) => (
              <React.Fragment key={ev.id}>
              {i === upcoming.length && past.length > 0 && <h2 className="font-title text-lg pt-4" style={{ color: 'var(--olive)' }} data-testid="events-past-heading">Past events</h2>}
              <div className="bg-white rounded-lg shadow-sm border p-5 flex flex-col sm:flex-row gap-4" style={{ borderColor: 'rgba(0,0,0,0.08)', opacity: ev.ended ? 0.85 : 1 }} data-testid={`event-${ev.id}`}>
                {ev.image_url && <img src={ev.image_url} alt={ev.title} className="w-full sm:w-40 h-32 object-contain rounded" style={{ background: 'rgba(0,0,0,0.04)' }} />}
                <div className="flex-1">
                  <h2 className="font-title text-xl" style={{ color: 'var(--navy)' }}>{ev.title}</h2>
                  <div className="flex flex-wrap gap-4 text-sm mt-1" style={{ color: 'var(--olive)' }}>
                    {ev.starts_at && <span className="flex items-center gap-1"><CalendarClock size={14} /> {fmt(ev.starts_at)}</span>}
                    {ev.location && <span className="flex items-center gap-1"><MapPin size={14} /> {ev.location}</span>}
                    {ev.capacity ? <span className="flex items-center gap-1"><Users size={14} /> {ev.spots_left} of {ev.capacity} left</span> : null}
                  </div>
                  {ev.description && <p className="text-sm mt-2" style={{ color: 'var(--ink)' }}>{ev.description}</p>}
                  <EventPhotoStrip photos={ev.photos} eventId={ev.id} eventTitle={ev.title} />
                  <div className="flex items-center gap-3 mt-3">
                    <span className="font-title" style={{ color: 'var(--maroon)' }}>
                      {ev.applicable_price > 0 ? `$${ev.applicable_price.toFixed(2)}` : 'Free'}
                      {ev.member_price < ev.price && ev.applicable_price === ev.member_price && ev.price > 0 && <span className="text-xs ml-1" style={{ color: 'var(--olive)' }}>member price</span>}
                    </span>
                    {ev.ended ? <span className="text-sm px-2 py-1 rounded" style={{ background: 'rgba(0,0,0,0.06)', color: 'var(--olive)' }} data-testid={`closed-${ev.id}`}>Registration closed</span>
                      : ev.sold_out ? <span className="text-sm px-2 py-1 rounded" style={{ background: '#fdecea', color: '#8a1f11' }} data-testid={`soldout-${ev.id}`}>Sold out</span>
                      : <button onClick={() => setReg(ev)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }} data-testid={`register-${ev.id}`}><Ticket size={15} /> Register</button>}
                  </div>
                </div>
              </div>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
      {reg && <RegisterModal ev={reg} onClose={() => setReg(null)} />}
    </AccessGate>
  );
}
